<template>
  <div class="contact-sheet">
    <router-view />
    <div class="header">
      <ShareModal
        v-if="$store.state.ui.share && $route.name === 'contact-sheet'"
        terminology="Collection"
      />

      <div
        class="menu"
        v-if="$store.state.isLandscape && $store.state.isMobile"
      >
        <MiniMobileHeaderBar />
      </div>

      <div class="stats">
        <div>
          <span class="current">{{
            formatNumber($store.getters.numImagesFiltered)
          }}</span
          ><span>{{
            " / " + formatNumber($store.getters.numImagesTotal) + " images"
          }}</span>
        </div>
        <div
          class="shuffle-icon"
          :title="
            $store.state.seed === 'chron'
              ? 'Turn shuffle on'
              : 'Turn shuffle off'
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="23"
            height="18"
            viewBox="0 0 23 18"
            :class="{ shuffle: true, active: $store.state.seed !== 'chron' }"
            @click="toggleShuffle"
          >
            <path
              fill="#C04323"
              d="M17.324 5.853H18.8v2.31L22.935 4.1 18.8.125v2.637h-1.476c-4.221 0-6.605 3.022-8.708 5.688-1.889 2.398-3.523 4.659-6.157 4.659H.065v3.09H2.46c4.222 0 6.606-3.213 8.71-5.879 1.888-2.397 3.516-4.467 6.155-4.467zM6.252 7.62c.179-.225.363-.454.547-.688.449-.567.918-1.167 1.429-1.763C6.717 3.807 4.894 2.8 2.46 2.8H.065v3.09s.68-.029 2.395 0c1.659.035 2.7.689 3.792 1.729zM18.8 13.133h-1.475c-1.608 0-2.844-.772-3.992-1.92-.112.147-.23.294-.347.44-.506.64-1.047 1.328-1.644 2.007 1.552 1.46 3.43 2.564 5.983 2.564H18.8v2.65l4.135-3.974-4.135-4.062v2.295z"
            />
          </svg>
        </div>
      </div>

      <div class="filters">
        <MadLibs @changed="numImages = 20" />
      </div>

      <div class="buttons">
        <HelpButton @click="$store.dispatch('showSortTour')" />
        <div class="surprise-help-content">
          <div class="surprise-container">
            <div class="surprise" @click="$store.dispatch('setRandomFilter')">
              <span>Surprise me!</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="content" @scroll="handleScroll">
      <StripMap
        v-if="!$store.state.isMobile"
        circleColor="#fff"
        :hovering="imageHovering"
        :locations="stripMapPoints"
        :styleOverride="{ fillOpacity: 0.5, strokeWidth: 0 }"
        :invert="false"
      />

      <div class="images">
        <div
          v-for="(image, index) in ($store.state.seed === 'chron'
            ? $store.getters.filteredImages
            : $store.getters.shuffledFilteredImages
          ).slice(0, numImages)"
          :key="'https://media.getty.edu/iiif/image/' + image.id"
          class="image card interactive"
          @click="
            $router.push({
              name: 'ContactSheetModal',
              params: {
                image: image.id
              },
              query: $route.query
            })
          "
          @mouseenter="imageHovering = image.id"
          @mouseleave="imageHovering = null"
          :style="{
            transform:
              'rotate(' +
              (Math.sin(
                index * 10000 + 1 + (parseFloat($store.state.seed) || 0)
              ) -
                Math.floor(
                  Math.sin(
                    index * 10000 + 1 + (parseFloat($store.state.seed) || 0)
                  )
                ) -
                0.5) *
                1.5 +
              'deg)'
          }"
        >
          <ProgressiveImage
            :filename="'https://media.getty.edu/iiif/image/' + image.id"
            :maxres="400"
          />
        </div>
      </div>
      <div
        class="load-more button"
        @click="loadmore"
        v-if="$store.getters.numImagesFiltered > numImages"
      >
        Load more images
      </div>
      <Footer v-if="!$store.state.isMobile" />
    </div>
  </div>
</template>

<script>
import HelpButton from "../components/HelpButton";
import ProgressiveImage from "../components/ProgressiveImage";
import StripMap from "../components/StripMap";
import MadLibs from "../components/MadLibs";
import util from "../constants/util";
import ShareModal from "../components/ShareModal";
import Footer from "../components/Footer";
import MiniMobileHeaderBar from "../components/MiniMobileHeaderBar";

export default {
  name: "ContactSheet",
  components: {
    HelpButton,
    ProgressiveImage,
    StripMap,
    MadLibs,
    ShareModal,
    Footer,
    MiniMobileHeaderBar
  },
  mounted() {
    if (!this.$store.state.seed) {
      let seed = Math.round(Math.random() * 999);
      this.$store.commit("setSeed", { seed, pushToURL: { seed } });
    }
  },
  data() {
    return {
      imageHovering: null,
      numImages: 20
    };
  },
  computed: {
    stripMapPoints() {
      const { totalDistance } = this.$store.getters;
      return this.$store.getters.shuffledFilteredImages
        .slice(0, this.numImages * 20)
        .map(i => ({
          id: i.id,
          position: i.location / totalDistance
        }));
    }
  },
  methods: {
    loadmore() {
      this.numImages += 20;
    },

    handleScroll({ target }) {
      const distanceFromBottom =
        target.scrollHeight - target.scrollTop - target.offsetHeight;
      if (distanceFromBottom < 50) {
        this.loadmore();
      }
    },

    toggleShuffle() {
      if (this.$store.state.seed === "chron") {
        let seed = Math.round(Math.random() * 999);
        this.$store.commit("setSeed", { seed, pushToURL: { seed } });
      } else {
        this.$store.commit("setSeed", {
          seed: "chron",
          pushToURL: { seed: "chron" }
        });
      }
    }
  },
  mixins: [util]
};
</script>

<style scoped lang="scss">
@import "@/constants/_variables.scss";

.contact-sheet {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.header {
  background-color: white;
  position: relative;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 10px;
  padding-bottom: 10px;

  @media (max-width: 815px) {
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  @media (max-width: 815px) and (orientation: landscape) {
    min-height: 120px;
  }
}

.content {
  flex-grow: 1;
  margin-bottom: 25px;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  background-color: black;
}

.images {
  display: flex;
  flex-wrap: wrap;
  margin: 5px;

  .image {
    width: calc(20% - 1em);
    margin: 0.5em;
    transition: 1s transform;

    @media (max-width: 1815px) {
      width: calc(25% - 1em);
    }

    @media (max-width: 1300px) {
      width: calc(33% - 1em);
    }

    @media (max-width: 815px) {
      width: calc(50% - 1em);
    }

    @media (max-width: 815px) and (orientation: landscape) {
      width: calc(33% - 1em);
    }

    img {
      width: 100%;
      margin: 0;
      padding: 0;
      vertical-align: top;

      &:hover {
        box-shadow: 0px 0px 12px rgba(255, 255, 255, 0.8);
      }
    }
  }
}

.load-more {
  max-width: 250px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1em;
  display: block;
  text-transform: uppercase;
}

.stats {
  text-align: left;
  letter-spacing: 0.4px;
  font-size: 14px;
  display: flex;
  align-items: center;

  svg {
    margin-left: 0.5em;
  }

  @media (max-width: 815px) {
    order: 1;
    margin-top: 0.5em;
  }

  @media (max-width: 815px) and (orientation: landscape) {
    position: absolute;
    right: 0px;
    bottom: 0px;
  }
}

.clear {
  font-weight: 500;
  padding: 0.25em;
  cursor: pointer;
  color: $copper;
  background-color: $very-light-pink;
  text-transform: uppercase;
  margin-bottom: 0.5em;

  &:hover {
    background-color: $light-gold;
    color: $copper;
  }
}

.surprise-container {
  height: 40px;
  position: relative;
  margin: 0 20px;

  @media (max-width: 815px) and (orientation: portrait) {
    height: 30px;
    transform: rotate(-90deg) translateY(-15px);
    transform-origin: 100% 0%;
    right: 0px;
    top: 30px;
  }

  @media (max-width: 815px) and (orientation: landscape) {
    height: 30px;
    width: 120px;
  }

  .surprise {
    height: 40px;
    font-weight: 500;
    font-style: italic;
    padding: 0.25em;
    cursor: pointer;
    color: $copper;
    font-size: 18px;
    background-color: $light-gold;
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background-color: $copper;
      color: $white;
    }

    @media (max-width: 815px) {
      font-size: 16px;
      height: 30px;
    }

    span {
      z-index: 2;
    }
  }

  .surprise:after {
    cursor: pointer;
    background: inherit;
    content: "";
    width: 40px;
    height: 100%;
    right: -20px;
    bottom: 0;
    display: block;
    position: absolute;
    transform-origin: 100%;
    transform: skewX(-10deg);
    z-index: 1;
    transition: 0.3s transform;
  }

  .surprise:before {
    cursor: pointer;
    background: inherit;
    content: "";
    width: 40px;
    height: 100%;
    left: -20px;
    bottom: 0;
    display: block;
    position: absolute;
    transform-origin: 100%;
    transform: skewX(-10deg);
    z-index: 1;
    transition: 0.3s transform;
  }

  &:hover {
    .surprise:after,
    .surprise:before {
      transform: skewX(10deg);
    }
  }
}

.filters {
  padding: 0.5rem;
  text-align: center;

  @media (max-width: 815px) {
    max-width: calc(100% - 55px);
  }

  @media (max-width: 815px) and (orientation: landscape) {
    margin-bottom: 10px;
    max-width: calc(100% - 200px);
  }
}

.current {
  font-weight: 500;
}

.shuffle {
  cursor: pointer;

  path {
    fill: $brown-grey;
  }

  &.active {
    path {
      fill: $copper;
    }
  }
}

.buttons {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding-top: 0.5rem;

  .HelpButton {
    margin-bottom: 0.5rem;
  }

  @media (max-width: 815px) and (orientation: portrait) {
    align-items: flex-start;
    flex-direction: row;
    top: 0;
    right: 0;
    position: absolute;

    .HelpButton {
      margin-right: -100px;
      margin-top: 10px;
    }
  }

  @media (max-width: 815px) and (orientation: landscape) {
    position: absolute;
    justify-content: center;
    right: 10px;
    bottom: 30px;
    height: 100px;
  }
}

.menu {
  position: absolute;
  top: 0px;
  right: 0px;
}

.shuffle-icon {
  margin-bottom: -2px;

  svg:hover {
    path {
      fill: #8e2f17 !important;
    }
  }
}
</style>

<style lang="scss">
.contact-sheet {
  #footer {
    height: 25px;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
  }
}
</style>
